import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DialogActions } from 'src/app/model/dictionary';
import { AdminDialogResult } from '../admin-dialog-result';

export interface AdminGroupDialogData {
  mode: 'create' | 'rename';
  name?: string;
  existingNames: Set<string>;
}

export type AdminGroupDialogResult = AdminDialogResult<{ name: string }>;

const nameUniqueValidator = (existingNames: Set<string>): ValidatorFn => (control: AbstractControl): ValidationErrors | null => {
  const value = (control.value as string | null)?.trim().toLowerCase();
  return value && existingNames.has(value) ? { nameAlreadyUsed: true } : null;
};

@Component({
  selector: 'app-admin-group-dialog',
  templateUrl: './admin-group-dialog.component.html',
  styleUrls: ['./admin-group-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminGroupDialogComponent {
  private readonly dialogRef = inject<MatDialogRef<AdminGroupDialogComponent, AdminGroupDialogResult>>(MatDialogRef);
  protected readonly data = inject<AdminGroupDialogData>(MAT_DIALOG_DATA);

  protected readonly form = new FormGroup({
    name: new FormControl(this.data.name ?? '', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(2), nameUniqueValidator(this.data.existingNames)],
    }),
  });

  protected get isCreating(): boolean {
    return this.data.mode === 'create';
  }

  protected save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.dialogRef.close({ event: DialogActions.CONFIRM, data: { name: this.form.getRawValue().name.trim() } });
  }

  protected closeDialog(): void {
    this.dialogRef.close({ event: DialogActions.REJECT });
  }
}